import { useState, useEffect } from 'react';
import { matchesApi, jobsApi, companiesApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SearchableSelect } from '../ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Slider } from '../ui/slider';
import { 
  Filter, ChevronDown, ChevronUp, X, Save, Trash2, 
  Search, Building2, Briefcase, Calendar, Star, Send,
  CheckCircle, Clock, XCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdvancedFilters({ filters, onFilterChange, onApply, onReset }) {
  const [expanded, setExpanded] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  // Presets
  const [presets, setPresets] = useState([]);
  const [savePresetModal, setSavePresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  
  // Local filter state
  const [localFilters, setLocalFilters] = useState({
    search: '',
    job_id: '',
    company_id: '',
    share_status: 'all',
    hr_status: 'all',
    min_score: 0,
    max_score: 100,
    skills: [],
    date_from: '',
    date_to: '',
    sort_by: 'overall_score',
    sort_order: 'desc',
    ...filters
  });

  useEffect(() => {
    loadFilterOptions();
    loadPresets();
  }, []);

  useEffect(() => {
    setLocalFilters(prev => ({ ...prev, ...filters }));
  }, [filters]);

  const loadFilterOptions = async () => {
    setLoadingOptions(true);
    try {
      const res = await matchesApi.getFilterOptions();
      setFilterOptions(res.data);
    } catch (err) {
      console.error('Failed to load filter options');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadPresets = async () => {
    try {
      const res = await matchesApi.getFilterPresets();
      setPresets(res.data.presets || []);
    } catch (err) {
      console.error('Failed to load presets');
    }
  };

  const handleFilterChange = (key, value) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const handleSkillToggle = (skill) => {
    const currentSkills = localFilters.skills || [];
    const updated = currentSkills.includes(skill)
      ? currentSkills.filter(s => s !== skill)
      : [...currentSkills, skill];
    handleFilterChange('skills', updated);
  };

  const handleApply = () => {
    onApply(localFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      search: '',
      job_id: '',
      company_id: '',
      share_status: 'all',
      hr_status: 'all',
      min_score: 0,
      max_score: 100,
      skills: [],
      date_from: '',
      date_to: '',
      sort_by: 'overall_score',
      sort_order: 'desc'
    };
    setLocalFilters(resetFilters);
    onReset();
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }
    setSavingPreset(true);
    try {
      await matchesApi.saveFilterPreset(presetName.trim(), localFilters);
      toast.success('Preset saved successfully');
      setSavePresetModal(false);
      setPresetName('');
      loadPresets();
    } catch (err) {
      toast.error('Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await matchesApi.deleteFilterPreset(presetId);
      toast.success('Preset deleted');
      loadPresets();
    } catch (err) {
      toast.error('Failed to delete preset');
    }
  };

  const handleLoadPreset = (preset) => {
    const newFilters = { ...localFilters, ...preset.filters };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
    onApply(newFilters);
    toast.success(`Loaded preset: ${preset.name}`);
  };

  const activeFilterCount = Object.entries(localFilters).filter(([key, value]) => {
    if (key === 'search' && value) return true;
    if (key === 'job_id' && value) return true;
    if (key === 'company_id' && value) return true;
    if (key === 'share_status' && value !== 'all') return true;
    if (key === 'hr_status' && value !== 'all') return true;
    if (key === 'min_score' && value > 0) return true;
    if (key === 'max_score' && value < 100) return true;
    if (key === 'skills' && value?.length > 0) return true;
    if (key === 'date_from' && value) return true;
    if (key === 'date_to' && value) return true;
    return false;
  }).length;

  return (
    <div className="space-y-3">
      {/* Compact filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search candidates..."
            value={localFilters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="pl-9 h-9"
            data-testid="search-candidates-input"
          />
        </div>

        {/* Quick filters */}
        <Select value={localFilters.share_status} onValueChange={(v) => handleFilterChange('share_status', v)}>
          <SelectTrigger className="w-[140px] h-9" data-testid="share-status-filter">
            <Send className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Share Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Matches</SelectItem>
            <SelectItem value="sent">Sent to HR</SelectItem>
            <SelectItem value="not_sent">Not Sent</SelectItem>
          </SelectContent>
        </Select>

        <Select value={localFilters.hr_status} onValueChange={(v) => handleFilterChange('hr_status', v)}>
          <SelectTrigger className="w-[150px] h-9" data-testid="hr-status-filter">
            <SelectValue placeholder="HR Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">
              <span className="flex items-center gap-2"><Clock className="w-3 h-3 text-amber-500" /> Pending</span>
            </SelectItem>
            <SelectItem value="shortlisted">
              <span className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-500" /> Shortlisted</span>
            </SelectItem>
            <SelectItem value="rejected">
              <span className="flex items-center gap-2"><XCircle className="w-3 h-3 text-red-500" /> Rejected</span>
            </SelectItem>
            <SelectItem value="on_hold">
              <span className="flex items-center gap-2"><Clock className="w-3 h-3 text-orange-500" /> On Hold</span>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Advanced filters toggle */}
        <Button
          variant={expanded ? "default" : "outline"}
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="h-9"
          data-testid="advanced-filters-toggle"
        >
          <Filter className="w-4 h-4 mr-2" />
          Advanced
          {activeFilterCount > 0 && (
            <Badge className="ml-2 bg-blue-500 text-white">{activeFilterCount}</Badge>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
        </Button>

        {/* Presets dropdown */}
        {presets.length > 0 && (
          <Select onValueChange={(presetId) => {
            const preset = presets.find(p => p.id === presetId);
            if (preset) handleLoadPreset(preset);
          }}>
            <SelectTrigger className="w-[140px] h-9">
              <Star className="w-4 h-4 mr-2 text-amber-500" />
              <SelectValue placeholder="Presets" />
            </SelectTrigger>
            <SelectContent>
              {presets.map(preset => (
                <SelectItem key={preset.id} value={preset.id}>
                  {preset.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Apply & Reset */}
        <Button size="sm" onClick={handleApply} className="h-9 bg-blue-600 hover:bg-blue-700" data-testid="apply-filters-btn">
          Apply
        </Button>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={handleReset} className="h-9 text-slate-500" data-testid="reset-filters-btn">
            <X className="w-4 h-4 mr-1" /> Reset
          </Button>
        )}
      </div>

      {/* Expanded filters panel */}
      {expanded && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="p-4">
            {loadingOptions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Job Filter */}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> Job/Position
                  </label>
                  <SearchableSelect
                    value={localFilters.job_id || ''}
                    onValueChange={(v) => handleFilterChange('job_id', v)}
                    options={(filterOptions?.jobs || []).map(job => ({
                      id: job.id,
                      name: job.title + (job.company ? ` @ ${job.company}` : '')
                    }))}
                    placeholder="All Jobs"
                    searchPlaceholder="Search jobs..."
                    emptyMessage="No jobs found"
                    displayKey="name"
                    valueKey="id"
                    className="h-9"
                    data-testid="job-filter"
                  />
                </div>

                {/* Company Filter */}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> Company
                  </label>
                  <SearchableSelect
                    value={localFilters.company_id || ''}
                    onValueChange={(v) => handleFilterChange('company_id', v)}
                    options={(filterOptions?.companies || []).map(c => ({
                      id: c.id,
                      name: c.name
                    }))}
                    placeholder="All Companies"
                    searchPlaceholder="Search companies..."
                    emptyMessage="No companies found"
                    displayKey="name"
                    valueKey="id"
                    className="h-9"
                    data-testid="company-filter"
                  />
                </div>

                {/* Date From */}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Date From
                  </label>
                  <Input
                    type="date"
                    value={localFilters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="h-9"
                    data-testid="date-from-filter"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Date To
                  </label>
                  <Input
                    type="date"
                    value={localFilters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="h-9"
                    data-testid="date-to-filter"
                  />
                </div>

                {/* Score Range */}
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                    <Star className="w-3 h-3" /> Match Score Range: {localFilters.min_score}% - {localFilters.max_score}%
                  </label>
                  <div className="flex items-center gap-4 mt-2">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={localFilters.min_score}
                      onChange={(e) => handleFilterChange('min_score', parseInt(e.target.value) || 0)}
                      className="w-20 h-9"
                      data-testid="min-score-input"
                    />
                    <div className="flex-1">
                      <Slider
                        value={[localFilters.min_score, localFilters.max_score]}
                        onValueChange={([min, max]) => {
                          setLocalFilters(prev => ({ ...prev, min_score: min, max_score: max }));
                        }}
                        min={0}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={localFilters.max_score}
                      onChange={(e) => handleFilterChange('max_score', parseInt(e.target.value) || 100)}
                      className="w-20 h-9"
                      data-testid="max-score-input"
                    />
                  </div>
                </div>

                {/* Sort Options */}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5">Sort By</label>
                  <Select value={localFilters.sort_by} onValueChange={(v) => handleFilterChange('sort_by', v)}>
                    <SelectTrigger className="h-9" data-testid="sort-by-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="overall_score">Match Score</SelectItem>
                      <SelectItem value="created_at">Date Created</SelectItem>
                      <SelectItem value="candidate_name">Candidate Name</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5">Order</label>
                  <Select value={localFilters.sort_order} onValueChange={(v) => handleFilterChange('sort_order', v)}>
                    <SelectTrigger className="h-9" data-testid="sort-order-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Highest First</SelectItem>
                      <SelectItem value="asc">Lowest First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Skills Filter */}
                <div className="md:col-span-2 lg:col-span-4">
                  <label className="text-xs font-medium text-slate-600 mb-1.5">Filter by Skills</label>
                  <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-2 bg-white rounded-lg border">
                    {filterOptions?.skills?.slice(0, 50).map(skill => (
                      <button
                        key={skill}
                        onClick={() => handleSkillToggle(skill)}
                        className={`px-2 py-1 rounded-full text-xs transition-colors ${
                          localFilters.skills?.includes(skill)
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`skill-filter-${skill}`}
                      >
                        {skill}
                        {localFilters.skills?.includes(skill) && <X className="w-3 h-3 ml-1 inline" />}
                      </button>
                    ))}
                    {(!filterOptions?.skills || filterOptions.skills.length === 0) && (
                      <p className="text-xs text-slate-400">No skills available</p>
                    )}
                  </div>
                </div>

                {/* Save Preset Button */}
                <div className="md:col-span-2 lg:col-span-4 flex items-center justify-between border-t pt-3 mt-2">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSavePresetModal(true)}
                      className="h-8"
                      data-testid="save-preset-btn"
                    >
                      <Save className="w-4 h-4 mr-1" /> Save as Preset
                    </Button>
                    {presets.length > 0 && (
                      <span className="text-xs text-slate-400">{presets.length} preset(s) saved</span>
                    )}
                  </div>
                  
                  {/* Preset management */}
                  {presets.length > 0 && (
                    <div className="flex items-center gap-1">
                      {presets.slice(0, 3).map(preset => (
                        <div key={preset.id} className="flex items-center gap-1 bg-slate-100 rounded-full pl-3 pr-1 py-1">
                          <button
                            onClick={() => handleLoadPreset(preset)}
                            className="text-xs font-medium text-slate-700 hover:text-blue-600"
                          >
                            {preset.name}
                          </button>
                          <button
                            onClick={() => handleDeletePreset(preset.id)}
                            className="p-1 hover:bg-red-100 rounded-full text-slate-400 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save Preset Modal */}
      <Dialog open={savePresetModal} onOpenChange={setSavePresetModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-blue-500" />
              Save Filter Preset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Preset Name</label>
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g., High-score React developers"
                data-testid="preset-name-input"
              />
            </div>
            <div className="text-xs text-slate-500">
              <p className="font-medium mb-1">Current filters to save:</p>
              <div className="flex flex-wrap gap-1">
                {localFilters.search && <Badge variant="outline">Search: {localFilters.search}</Badge>}
                {localFilters.share_status !== 'all' && <Badge variant="outline">Share: {localFilters.share_status}</Badge>}
                {localFilters.hr_status !== 'all' && <Badge variant="outline">HR Status: {localFilters.hr_status}</Badge>}
                {(localFilters.min_score > 0 || localFilters.max_score < 100) && (
                  <Badge variant="outline">Score: {localFilters.min_score}-{localFilters.max_score}%</Badge>
                )}
                {localFilters.skills?.length > 0 && (
                  <Badge variant="outline">{localFilters.skills.length} skill(s)</Badge>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetModal(false)}>Cancel</Button>
            <Button onClick={handleSavePreset} disabled={savingPreset} className="bg-blue-600 hover:bg-blue-700">
              {savingPreset ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
